// Déroulé du jour J : types et calculs partagés entre l'espace mariés, le lien
// de l'équipe et le programme du site des invités.

// Heures en minutes depuis minuit du jour du moment ; au-delà de 1440, c'est
// après minuit (la soirée continue sur le même jour du déroulé).
export interface DayplanMoment {
  id: number;
  day: string; // 'AAAA-MM-JJ'
  start: number;
  duration: number;
  title: string;
  place: string | null;
  note: string | null;
  guestNote: string | null;
  guests: boolean;
  people: string[]; // références : c1/c2 (mariés), p<id> (ajouté à la main), e<id> (dépense du budget)
  eventId?: number | null; // événement du mariage relié : même heure et même nom des deux côtés
}

export interface DayplanPersonRow {
  id: number;
  kind: 'team' | 'vendor';
  name: string;
  role: string | null;
  phone: string | null;
}

export interface BudgetVendor {
  ref: string;
  name: string;
  role: string;
  phone: string | null;
}

// Personne telle qu'affichée : mariés, proches, prestataires (à la main ou du budget).
export interface DayplanPerson {
  ref: string;
  name: string;
  role: string;
  phone: string | null;
  source: 'couple' | 'team' | 'vendor' | 'budget';
  id?: number; // personne ajoutée à la main (modifiable)
}

export const DAY_MINUTES = 24 * 60;
const NIGHT_UNTIL = 5 * 60; // une heure saisie avant 5 h est la nuit qui suit

export const endOf = (m: { start: number; duration: number }) => m.start + m.duration;

export function hhmm(min: number): string {
  const h = Math.floor(min / 60) % 24;
  const m = ((min % 60) + 60) % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// « 19:30 » saisi dans un champ heure → minutes, la nuit comptant après minuit.
export function fromTimeInput(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!m) return null;
  const min = Number(m[1]) * 60 + Number(m[2]);
  return min < NIGHT_UNTIL ? min + DAY_MINUTES : min;
}

export function durationLabel(d: number): string {
  if (d < 60) return `${d} min`;
  const h = Math.floor(d / 60);
  const r = d % 60;
  return r ? `${h} h ${String(r).padStart(2, '0')}` : `${h} h`;
}

export const sortMoments = <T extends DayplanMoment>(list: T[]) =>
  [...list].sort((a, b) => a.day.localeCompare(b.day) || a.start - b.start || a.id - b.id);

// 'AAAA-MM-JJ' décalé de n jours.
export function addDays(day: string, n: number): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function dayDate(day: string): Date {
  return new Date(`${day}T12:00:00`);
}

export function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Jours affichés : veille, jour J, lendemain, et tout autre jour qui a des moments.
export function dayList(weddingDate: string | null | undefined, moments: { day: string }[]): string[] {
  const days = new Set(moments.map((m) => m.day));
  if (weddingDate) [-1, 0, 1].forEach((n) => days.add(addDays(weddingDate, n)));
  return Array.from(days).sort();
}

// Grand moment de la journée sous lequel ranger un moment.
export type DayPart = 'morning' | 'afternoon' | 'evening' | 'night';
export const DAY_PART_LABEL: Record<DayPart, string> = {
  morning: 'Matin',
  afternoon: 'Après-midi',
  evening: 'Soirée',
  night: 'Nuit',
};
export function dayPart(start: number): DayPart {
  if (start < 12 * 60) return 'morning';
  if (start < 18 * 60) return 'afternoon';
  if (start < DAY_MINUTES) return 'evening';
  return 'night';
}

// Moments qui commencent avant la fin d'un autre et partagent quelqu'un.
export interface Overlap {
  other: string;
  minutes: number;
  people: string[];
}
export function overlaps(list: DayplanMoment[]): Record<number, Overlap> {
  const res: Record<number, Overlap> = {};
  const sorted = sortMoments(list);
  sorted.forEach((a, i) =>
    sorted.slice(i + 1).forEach((b) => {
      const shared = a.people.filter((p) => b.people.includes(p));
      if (b.day === a.day && b.start < endOf(a) && shared.length && !res[b.id]) {
        res[b.id] = { other: a.title, minutes: Math.min(endOf(a), endOf(b)) - b.start, people: shared };
      }
    }),
  );
  return res;
}

// Toutes les personnes du déroulé, indexées par référence.
export function resolvePeople(
  couple: { partner1?: string | null; partner2?: string | null },
  people: DayplanPersonRow[],
  vendors: BudgetVendor[],
  t: (s: string) => string,
): DayplanPerson[] {
  const list: DayplanPerson[] = [];
  if (couple.partner1) list.push({ ref: 'c1', name: couple.partner1, role: t('Les mariés'), phone: null, source: 'couple' });
  if (couple.partner2) list.push({ ref: 'c2', name: couple.partner2, role: t('Les mariés'), phone: null, source: 'couple' });
  people
    .filter((p) => p.kind === 'team')
    .forEach((p) => list.push({ ref: `p${p.id}`, id: p.id, name: p.name, role: p.role || t('Proche'), phone: p.phone, source: 'team' }));
  people
    .filter((p) => p.kind === 'vendor')
    .forEach((p) => list.push({ ref: `p${p.id}`, id: p.id, name: p.name, role: p.role || t('Prestataire'), phone: p.phone, source: 'vendor' }));
  vendors.forEach((v) => list.push({ ref: v.ref, name: v.name, role: v.role, phone: v.phone, source: 'budget' }));
  return list;
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

// Teinte de pastille : mariés, proches, prestataires ajoutés, prestataires du budget.
export const TONE: Record<DayplanPerson['source'], number> = { couple: 0, team: 1, vendor: 2, budget: 3 };
